import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useTheme } from './ThemeContext';

interface AccentContextType {
  accent: string;        // base hex — the chosen (or default) accent
  accentDim: string;     // derived dim tint for the active theme
  setAccent: (color: string) => Promise<void>;
  refreshSettings: () => void;
}

const AccentContext = createContext<AccentContextType | undefined>(undefined);

const DEFAULT_ACCENT = '#0d7377';

/* ── Accent ramp ──────────────────────────────────────────────────────────
   The picker stores ONE base hex. From it we derive the full 4-stop ramp
   (--accent / --accent-hover / --accent-mid / --accent-dim) tuned for the
   ACTIVE theme and write all four as inline vars on <html>. Same derivation
   as the sibling apps (PMO, References). */

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  const int = m ? parseInt(m[1], 16) : 0x0d7377;
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = ln - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

interface Ramp { accent: string; accentHover: string; accentMid: string; accentDim: string; }

function deriveRamp(baseHex: string, theme: 'light' | 'dark'): Ramp {
  const { h, s, l } = hexToHsl(baseHex);
  if (theme === 'dark') {
    const dS = Math.min(s, 82);
    const dL = clamp(l + 24, 62, 74);
    return {
      accent: hslToHex(h, dS, dL),
      accentHover: hslToHex(h, dS, Math.min(dL + 8, 82)),
      accentMid: hslToHex(h, clamp(dS - 12, 40, 72), clamp(l + 30, 58, 68)),
      accentDim: hslToHex(h, Math.min(s, 42), 15),
    };
  }
  return {
    accent: baseHex,
    accentHover: hslToHex(h, Math.min(s + 4, 100), Math.max(l - 12, 0)),
    accentMid: hslToHex(h, Math.max(s - 25, 0), Math.min(l + 30, 92)),
    accentDim: hslToHex(h, Math.min(s, 60), 96),
  };
}

function applyRamp(ramp: Ramp) {
  const root = document.documentElement.style;
  root.setProperty('--accent', ramp.accent);
  root.setProperty('--accent-hover', ramp.accentHover);
  root.setProperty('--accent-mid', ramp.accentMid);
  root.setProperty('--accent-dim', ramp.accentDim);
}

export function AccentProvider({ children }: { children: ReactNode }) {
  const { theme } = useTheme();
  const [accent, setAccentState] = useState(DEFAULT_ACCENT);
  const [settingsVersion, setSettingsVersion] = useState(0);

  const ramp = deriveRamp(accent, theme);

  useEffect(() => {
    applyRamp(ramp);
  }, [ramp.accent, ramp.accentHover, ramp.accentMid, ramp.accentDim]);

  const fetchSettings = useCallback(() => {
    fetch('/api/admin/settings', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((settings) => {
        if (settings?.accent_color) {
          setAccentState(settings.accent_color);
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings, settingsVersion]);

  const refreshSettings = useCallback(() => {
    setSettingsVersion((v) => v + 1);
  }, []);

  const setAccent = useCallback(async (color: string) => {
    setAccentState(color);
    await fetch('/api/admin/settings/accent_color', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ value: color }),
    });
  }, []);

  return (
    <AccentContext.Provider value={{ accent, accentDim: ramp.accentDim, setAccent, refreshSettings }}>
      {children}
    </AccentContext.Provider>
  );
}

export function useAccent() {
  const context = useContext(AccentContext);
  if (context === undefined) {
    throw new Error('useAccent must be used within an AccentProvider');
  }
  return context;
}
